import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { createGameAdUuid, isGameAdUuid } from './ids';
import {
  GAME_AD_CONFIG_QUERY_KEY,
  DEFAULT_GAME_AD_CONFIG,
  loadGameAdConfig,
  parseGameAdConfig,
} from './internal/config';
import { recordGameAdEvent } from './internal/events';
import {
  getGameAdFrequencyPolicy,
  getGameAdVariant,
  isGameAdsEnabled,
  shouldShowGameAdOpportunity,
} from './policy';
import { googleTestGameAdSdk, GameAdSdkError, type GameAdSdk } from './sdk';
import type {
  GameAdBannerStatus,
  GameAdConfig,
  GameAdEvent,
  GameAdEventType,
  GameAdFormat,
  GameAdOutcome,
  GameAdPlacement,
  GameAdReason,
  GameAdVariant,
  GameAdsSessionOptions,
} from './types';

const LABEL = 'Test advertisement';
const MAX_ANSWERED_QUESTION_COUNT = 1_000;
const MAX_REPORTED_ELAPSED_MS = 300_000;

export type GameAdsProviderProps = Readonly<{
  children: ReactNode;
  /** Development-only live-updatable configuration for the local ads fixture. */
  testConfigForTestOnly?: GameAdConfig;
  /** Development-only initial configuration for isolated local tests. */
  initialConfigForTestOnly?: GameAdConfig;
  /** Development-only Google test SDK or deterministic local fake. */
  sdkForTestOnly?: GameAdSdk;
  /** Development-only, local event sink. Events are never sent to Supabase. */
  onTestEventForTestOnly?: (event: GameAdEvent) => void;
}>;

type RequestInput = Readonly<{
  sessionId: string;
  options: GameAdsSessionOptions;
  userId: string;
  variant: GameAdVariant;
  format: Exclude<GameAdFormat, 'none' | 'banner'>;
  placement: 'between_rounds' | 'continue_playing' | 'streak_repair';
  opportunityId?: string;
  signal?: AbortSignal;
}>;

type ActiveRequest = {
  sessionId: string;
  controller: AbortController;
  cancelReason?: GameAdReason;
};

type SessionCounters = {
  interstitialRequests: number;
  rewardedRequests: number;
  lastInterstitialAt: number;
};

type CompletedReward = Readonly<{
  options: GameAdsSessionOptions;
  userId: string;
  config: GameAdConfig;
  variant: GameAdVariant;
  opportunityId: string;
}>;

type GameAdsContextValue = {
  config: GameAdConfig;
  configReady: boolean;
  testMode: boolean;
  activate: () => void;
  deactivate: () => void;
  emit: (event: GameAdEvent) => void;
  recordOffer: (event: GameAdEvent) => void;
  requestAd: (input: RequestInput) => Promise<GameAdOutcome>;
  cancelSession: (sessionId: string, reason: GameAdReason) => void;
  reportPreviewApplied: (sessionId: string, requestId: string) => void;
};

const NOOP_GAME_ADS_CONTEXT: GameAdsContextValue = Object.freeze({
  config: DEFAULT_GAME_AD_CONFIG,
  configReady: false,
  testMode: false,
  activate: () => {},
  deactivate: () => {},
  emit: () => {},
  recordOffer: () => {},
  requestAd: (input) =>
    Promise.resolve({
      status: 'disabled',
      requestId: input.opportunityId ?? createGameAdUuid(),
    }),
  cancelSession: () => {},
  reportPreviewApplied: () => {},
});

const GameAdsContext = createContext<GameAdsContextValue | null>(null);

function clampInteger(value: number, maximum: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(maximum, Math.trunc(value)));
}

function hasFormat(config: GameAdConfig, format: Exclude<GameAdFormat, 'none'>): boolean {
  if (format === 'interstitial') return config.interstitialEnabled;
  if (format === 'rewarded') return config.rewardedEnabled;
  return config.bannerEnabled;
}

function buildEvent(
  sessionId: string,
  options: GameAdsSessionOptions,
  userId: string,
  config: GameAdConfig,
  variant: GameAdVariant,
  input: {
    eventType: GameAdEventType;
    format: GameAdFormat;
    placement: GameAdPlacement;
    opportunityId?: string | null;
    reason?: GameAdReason;
    answeredQuestionCount?: number;
    elapsedMs?: number;
  }
): GameAdEvent {
  const elapsedMs =
    input.elapsedMs === undefined
      ? undefined
      : clampInteger(input.elapsedMs, MAX_REPORTED_ELAPSED_MS);
  const visitorCandidate = options.visitorId?.trim();
  const visitorId = isGameAdUuid(visitorCandidate) ? visitorCandidate : undefined;
  return {
    eventId: createGameAdUuid(),
    sessionId,
    opportunityId: input.opportunityId ?? null,
    configVersion: config.version,
    experimentId: config.experimentId,
    variant,
    userId,
    isAnonymous: options.isAnonymous,
    ...(visitorId ? { visitorId } : {}),
    gameMode: options.gameType,
    format: input.format,
    placement: input.placement,
    eventType: input.eventType,
    ...(input.reason ? { reason: input.reason } : {}),
    ...(elapsedMs === undefined ? {} : { elapsedMs }),
    answeredQuestionCount: clampInteger(
      input.answeredQuestionCount ?? options.answeredQuestionCount,
      MAX_ANSWERED_QUESTION_COUNT
    ),
  };
}

function createAbortPromise(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) =>
    signal.addEventListener('abort', () => resolve(), { once: true })
  );
}

function getLocalReturnKey(experimentId: string, actorId: string): string {
  return `atrivial-game-ads:${experimentId}:${actorId}`;
}

export function GameAdsProvider({
  children,
  testConfigForTestOnly,
  initialConfigForTestOnly,
  sdkForTestOnly,
  onTestEventForTestOnly,
}: GameAdsProviderProps) {
  const activeSessionCount = useRef(0);
  const [activeSessionCountState, setActiveSessionCount] = useState(0);
  const activeRequests = useRef(new Map<string, ActiveRequest>());
  const pendingRequests = useRef(new Map<string, Promise<GameAdOutcome>>());
  const countersBySession = useRef(new Map<string, SessionCounters>());
  const interstitialOpportunitiesBySession = useRef(new Map<string, Set<string>>());
  const rewardedOpportunitiesBySession = useRef(new Map<string, Set<string>>());
  const reportedOffersBySession = useRef(new Map<string, Set<string>>());
  const completedRewards = useRef(new Map<string, CompletedReward>());
  const previewReports = useRef(new Set<string>());

  const useTestConfig = import.meta.env.DEV && (testConfigForTestOnly ?? initialConfigForTestOnly);
  const testConfig = useTestConfig
    ? parseGameAdConfig(testConfigForTestOnly ?? initialConfigForTestOnly)
    : null;
  const localTestMode = Boolean(useTestConfig || (import.meta.env.DEV && onTestEventForTestOnly));
  const configQuery = useQuery({
    queryKey: GAME_AD_CONFIG_QUERY_KEY,
    queryFn: loadGameAdConfig,
    enabled: activeSessionCountState > 0 && !localTestMode,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: activeSessionCountState > 0 && !localTestMode ? 15_000 : false,
  });
  const config = testConfig ?? configQuery.data ?? DEFAULT_GAME_AD_CONFIG;
  const configReady = Boolean(testConfig) || (configQuery.isSuccess && !configQuery.isError);
  const sdk = import.meta.env.DEV && sdkForTestOnly ? sdkForTestOnly : googleTestGameAdSdk;

  const activate = useCallback(() => {
    activeSessionCount.current += 1;
    setActiveSessionCount(activeSessionCount.current);
  }, []);
  const deactivate = useCallback(() => {
    activeSessionCount.current = Math.max(0, activeSessionCount.current - 1);
    setActiveSessionCount(activeSessionCount.current);
  }, []);

  const emit = useCallback(
    (event: GameAdEvent) => {
      if (localTestMode && import.meta.env.DEV) {
        try {
          onTestEventForTestOnly?.(event);
        } catch {
          // A local fixture event sink must never interrupt gameplay.
        }
        return;
      }
      void recordGameAdEvent(event);
    },
    [localTestMode, onTestEventForTestOnly]
  );

  const recordOffer = useCallback(
    (event: GameAdEvent) => {
      if (event.eventType !== 'offer' || !event.opportunityId) return;
      const offers = reportedOffersBySession.current.get(event.sessionId) ?? new Set<string>();
      if (offers.has(event.opportunityId)) return;
      offers.add(event.opportunityId);
      reportedOffersBySession.current.set(event.sessionId, offers);
      emit(event);
    },
    [emit]
  );

  const cancelSession = useCallback((sessionId: string, reason: GameAdReason) => {
    activeRequests.current.forEach((active) => {
      if (active.sessionId !== sessionId) return;
      active.cancelReason = reason;
      active.controller.abort();
    });
    countersBySession.current.delete(sessionId);
    interstitialOpportunitiesBySession.current.delete(sessionId);
    rewardedOpportunitiesBySession.current.delete(sessionId);
    reportedOffersBySession.current.delete(sessionId);
    for (const pendingKey of pendingRequests.current.keys()) {
      if (pendingKey.startsWith(`${sessionId}:`)) pendingRequests.current.delete(pendingKey);
    }
    for (const requestKey of completedRewards.current.keys()) {
      if (requestKey.startsWith(`${sessionId}:`)) completedRewards.current.delete(requestKey);
    }
    for (const requestKey of previewReports.current) {
      if (requestKey.startsWith(`${sessionId}:`)) previewReports.current.delete(requestKey);
    }
  }, []);

  const reportPreviewApplied = useCallback(
    (sessionId: string, requestId: string) => {
      const key = `${sessionId}:${requestId}`;
      const completed = completedRewards.current.get(key);
      if (!completed || previewReports.current.has(key)) return;
      previewReports.current.add(key);
      emit(
        buildEvent(
          sessionId,
          completed.options,
          completed.userId,
          completed.config,
          completed.variant,
          {
            eventType: 'preview_completed',
            format: 'rewarded',
            placement: 'streak_repair',
            opportunityId: completed.opportunityId,
            reason: 'preview_applied',
          }
        )
      );
    },
    [emit]
  );

  const requestAd = useCallback(
    (input: RequestInput): Promise<GameAdOutcome> => {
      const policy = getGameAdFrequencyPolicy(config);
      const answeredCount = clampInteger(
        input.options.answeredQuestionCount,
        MAX_ANSWERED_QUESTION_COUNT
      );
      const opportunityKey =
        input.format === 'rewarded' && input.placement === 'streak_repair'
          ? 'streak-repair-offer'
          : `${input.format}:${input.placement}:${answeredCount}`;
      const pendingKey = `${input.sessionId}:${opportunityKey}`;
      const existing = pendingRequests.current.get(pendingKey);
      if (existing) return existing;

      const opportunityId = input.opportunityId ?? createGameAdUuid();
      const requestId = opportunityId;
      const makeEvent = (eventType: GameAdEventType, reason?: GameAdReason, elapsedMs?: number) =>
        buildEvent(input.sessionId, input.options, input.userId, config, input.variant, {
          eventType,
          format: input.format,
          placement: input.placement,
          opportunityId,
          reason,
          elapsedMs,
          answeredQuestionCount: answeredCount,
        });

      const experimentConfigured =
        config.mode === 'test' && config.aggression > 0 && config.rolloutPercent > 0;
      const finishWithoutSdk = (status: GameAdOutcome['status'], reason?: GameAdReason) => {
        if (
          configReady &&
          input.options.eligible &&
          input.userId &&
          experimentConfigured &&
          hasFormat(config, input.format)
        ) {
          recordOffer(makeEvent('offer', reason));
          if (status === 'capped') emit(makeEvent('capped', reason ?? 'session_cap'));
        }
        return Promise.resolve<GameAdOutcome>({ status, requestId });
      };

      if (!configReady || !input.options.eligible || !input.userId) {
        return finishWithoutSdk('disabled', 'config_disabled');
      }
      if (
        config.mode !== 'test' ||
        config.aggression === 0 ||
        config.rolloutPercent === 0 ||
        !hasFormat(config, input.format)
      ) {
        return finishWithoutSdk('disabled', 'config_disabled');
      }
      if (input.signal?.aborted) return Promise.resolve({ status: 'cancelled', requestId });

      const counters = countersBySession.current.get(input.sessionId) ?? {
        interstitialRequests: 0,
        rewardedRequests: 0,
        lastInterstitialAt: 0,
      };
      countersBySession.current.set(input.sessionId, counters);

      if (input.format === 'interstitial') {
        if (answeredCount === 0 || answeredCount % policy.everyAnsweredQuestions !== 0) {
          return finishWithoutSdk('skipped', 'below_question_checkpoint');
        }
        const opportunities =
          interstitialOpportunitiesBySession.current.get(input.sessionId) ?? new Set<string>();
        if (opportunities.has(opportunityKey))
          return Promise.resolve({ status: 'skipped', requestId });
        opportunities.add(opportunityKey);
        interstitialOpportunitiesBySession.current.set(input.sessionId, opportunities);
        if (Date.now() - counters.lastInterstitialAt < policy.cooldownSeconds * 1_000) {
          return finishWithoutSdk('skipped', 'cooldown');
        }
        if (input.variant === 'control') {
          counters.lastInterstitialAt = Date.now();
          return finishWithoutSdk('skipped', 'control_group');
        }
        if (counters.interstitialRequests >= config.maxInterstitialsPerSession) {
          return finishWithoutSdk('capped', 'session_cap');
        }
      } else if (input.variant === 'control') {
        return finishWithoutSdk('skipped', 'control_group');
      } else {
        const opportunities =
          rewardedOpportunitiesBySession.current.get(input.sessionId) ?? new Set<string>();
        if (opportunities.has(opportunityKey))
          return Promise.resolve({ status: 'skipped', requestId });
        if (counters.rewardedRequests >= config.maxRewardedPerSession) {
          return finishWithoutSdk('capped', 'session_cap');
        }
        opportunities.add(opportunityKey);
        rewardedOpportunitiesBySession.current.set(input.sessionId, opportunities);
      }
      if (
        !shouldShowGameAdOpportunity({
          userId: input.options.visitorId || input.userId,
          experimentId: config.experimentId,
          sessionId: input.sessionId,
          format: input.format,
          opportunityKey,
          aggression: config.aggression,
        })
      ) {
        return finishWithoutSdk('skipped', 'aggression_sampled_out');
      }

      recordOffer(makeEvent('offer'));
      emit(makeEvent('request'));
      if (input.format === 'interstitial') {
        counters.interstitialRequests += 1;
        counters.lastInterstitialAt = Date.now();
      } else {
        counters.rewardedRequests += 1;
      }

      const controller = new AbortController();
      const active: ActiveRequest = { sessionId: input.sessionId, controller };
      activeRequests.current.set(requestId, active);
      const externalAbort = () => {
        active.cancelReason = 'user_cancelled';
        controller.abort();
      };
      input.signal?.addEventListener('abort', externalAbort, { once: true });
      const startedAt = performance.now();
      let shownReported = false;
      let sdkPromise: ReturnType<GameAdSdk['request']>;
      try {
        sdkPromise = sdk.request({
          requestId,
          format: input.format,
          placement: input.placement,
          signal: controller.signal,
          onShown: () => {
            if (shownReported || controller.signal.aborted) return;
            shownReported = true;
            emit(makeEvent('shown', undefined, performance.now() - startedAt));
          },
        });
      } catch (error) {
        sdkPromise = Promise.reject(error);
      }
      let timeoutId: number | undefined;
      const task = Promise.race([
        sdkPromise.then((result) => ({ kind: 'sdk' as const, result })),
        createAbortPromise(controller.signal).then(() => null),
        new Promise<null>((resolve) => {
          timeoutId = window.setTimeout(() => {
            active.cancelReason = 'sdk_timeout';
            controller.abort();
            resolve(null);
          }, 33_000);
        }),
      ])
        .then((settled): GameAdOutcome => {
          if (timeoutId !== undefined) window.clearTimeout(timeoutId);
          const result = settled && settled.kind === 'sdk' ? settled.result : null;
          if (result === null) {
            const reason = active.cancelReason ?? 'user_cancelled';
            if (reason === 'sdk_timeout') {
              emit(makeEvent('timeout', reason, performance.now() - startedAt));
              return { status: 'timeout', requestId };
            }
            emit(makeEvent('cancelled', reason, performance.now() - startedAt));
            return { status: 'cancelled', requestId };
          }
          const terminalType: Record<
            Extract<
              typeof result.status,
              'completed' | 'dismissed' | 'no_fill' | 'failed' | 'timeout' | 'cancelled'
            >,
            GameAdEventType
          > = {
            completed: 'completed',
            dismissed: 'dismissed',
            no_fill: 'no_fill',
            failed: 'failed',
            timeout: 'timeout',
            cancelled: 'cancelled',
          };
          const reason =
            result.reason ??
            (result.status === 'no_fill'
              ? 'sdk_no_fill'
              : result.status === 'timeout'
                ? 'sdk_timeout'
                : result.status === 'failed'
                  ? 'sdk_error'
                  : result.status === 'cancelled'
                    ? 'user_cancelled'
                    : undefined);
          emit(makeEvent(terminalType[result.status], reason, result.elapsedMs));
          if (input.format === 'rewarded' && result.status === 'completed') {
            completedRewards.current.set(`${input.sessionId}:${requestId}`, {
              options: input.options,
              userId: input.userId,
              config,
              variant: input.variant,
              opportunityId,
            });
          }
          return { status: result.status, requestId };
        })
        .catch((error: unknown): GameAdOutcome => {
          const reason: GameAdReason =
            error instanceof GameAdSdkError && error.code === 'load_failed'
              ? 'sdk_load_failed'
              : 'sdk_error';
          emit(makeEvent('failed', reason, performance.now() - startedAt));
          return { status: 'failed', requestId };
        })
        .finally(() => {
          if (timeoutId !== undefined) window.clearTimeout(timeoutId);
          input.signal?.removeEventListener('abort', externalAbort);
          activeRequests.current.delete(requestId);
        });
      pendingRequests.current.set(pendingKey, task);
      void task.finally(() => {
        if (pendingRequests.current.get(pendingKey) === task)
          pendingRequests.current.delete(pendingKey);
      });
      return task;
    },
    [config, configReady, emit, recordOffer, sdk]
  );

  const previousConfigSignature = useRef('');
  useLayoutEffect(() => {
    const signature = JSON.stringify(config);
    if (
      !configReady ||
      (previousConfigSignature.current && previousConfigSignature.current !== signature)
    ) {
      activeRequests.current.forEach((active) => {
        active.cancelReason = configReady ? 'config_changed' : 'config_disabled';
        active.controller.abort();
      });
    }
    previousConfigSignature.current = signature;
  }, [config, configReady]);

  const contextValue = useMemo<GameAdsContextValue>(
    () => ({
      config,
      configReady,
      testMode: config.mode === 'test',
      activate,
      deactivate,
      emit,
      recordOffer,
      requestAd,
      cancelSession,
      reportPreviewApplied,
    }),
    [
      config,
      configReady,
      activate,
      deactivate,
      emit,
      recordOffer,
      requestAd,
      cancelSession,
      reportPreviewApplied,
    ]
  );

  return <GameAdsContext.Provider value={contextValue}>{children}</GameAdsContext.Provider>;
}

function useGameAdsContextOrDisabled(): GameAdsContextValue {
  // Ads remain an optional enhancement: isolated/embedded gameplay without the root provider
  // stays usable and fails closed without loading the SDK, fetching config, or recording events.
  return useContext(GameAdsContext) ?? NOOP_GAME_ADS_CONTEXT;
}

type SessionActivity = {
  startRequested: boolean;
  startReported: boolean;
  completedRequested: boolean;
  completedReported: boolean;
  questionCounts: Set<number>;
  reportedQuestionCounts: Set<number>;
  returnReported: boolean;
  bannerOfferReported: boolean;
  bannerImpressionReported: boolean;
  bannerDismissReported: boolean;
  rewardOfferReportedVersion: number | null;
};

function createActivity(): SessionActivity {
  return {
    startRequested: false,
    startReported: false,
    completedRequested: false,
    completedReported: false,
    questionCounts: new Set(),
    reportedQuestionCounts: new Set(),
    returnReported: false,
    bannerOfferReported: false,
    bannerImpressionReported: false,
    bannerDismissReported: false,
    rewardOfferReportedVersion: null,
  };
}

// eslint-disable-next-line react-refresh/only-export-components -- Provider and hook are the single public runtime boundary.
export function useGameAdsSession(options: GameAdsSessionOptions) {
  const context = useGameAdsContextOrDisabled();
  const {
    config,
    configReady,
    emit,
    recordOffer,
    activate,
    deactivate,
    cancelSession,
    requestAd,
    reportPreviewApplied,
  } = context;
  const runKey = options.sessionKey ?? 'mounted-game';
  const sessionRef = useRef<{
    runKey: string;
    sessionId: string;
    activity: SessionActivity;
  } | null>(null);
  if (!sessionRef.current || sessionRef.current.runKey !== runKey) {
    sessionRef.current = { runKey, sessionId: createGameAdUuid(), activity: createActivity() };
  }
  const sessionId = sessionRef.current.sessionId;
  const activity = sessionRef.current.activity;
  const actorId = options.visitorId || options.userId;
  const variant = useMemo(
    () =>
      actorId
        ? getGameAdVariant({
            userId: actorId,
            experimentId: config.experimentId,
            rolloutPercent: config.rolloutPercent,
          })
        : 'control',
    [actorId, config.experimentId, config.rolloutPercent]
  );
  const adsEnabled =
    options.eligible && configReady && Boolean(options.userId) && isGameAdsEnabled(config, variant);

  const report = useCallback(
    (input: {
      eventType: GameAdEventType;
      format: GameAdFormat;
      placement: GameAdPlacement;
      opportunityId?: string | null;
      reason?: GameAdReason;
      answeredQuestionCount?: number;
      elapsedMs?: number;
    }) => {
      if (!options.eligible || !configReady || !options.userId) return;
      emit(buildEvent(sessionId, options, options.userId, config, variant, input));
    },
    [options, configReady, config, emit, sessionId, variant]
  );

  const flushActivity = useCallback(() => {
    if (!options.eligible || !configReady || !options.userId || !activity.startRequested) return;
    if (!activity.startReported) {
      report({
        eventType: 'session_started',
        format: 'none',
        placement: 'game_session',
        opportunityId: null,
        answeredQuestionCount: 0,
      });
      activity.startReported = true;
      const stableActorId = options.visitorId || options.userId;
      if (stableActorId && !activity.returnReported) {
        activity.returnReported = true;
        try {
          const key = getLocalReturnKey(config.experimentId, stableActorId);
          const priorRun = window.localStorage.getItem(key);
          window.localStorage.setItem(key, sessionId);
          if (priorRun && priorRun !== sessionId) {
            report({
              eventType: 'return',
              format: 'none',
              placement: 'game_session',
              opportunityId: null,
              reason: 'returned_from_prior_session',
              answeredQuestionCount: 0,
            });
          }
        } catch {
          // Retention telemetry still records the gameplay session when storage is unavailable.
        }
      }
    }
    activity.questionCounts.forEach((count) => {
      if (activity.reportedQuestionCounts.has(count)) return;
      report({
        eventType: 'question_answered',
        format: 'none',
        placement: 'game_session',
        opportunityId: null,
        answeredQuestionCount: count,
      });
      activity.reportedQuestionCounts.add(count);
    });
    if (activity.completedRequested && !activity.completedReported) {
      report({
        eventType: 'session_completed',
        format: 'none',
        placement: 'game_session',
        opportunityId: null,
        answeredQuestionCount: options.answeredQuestionCount,
      });
      activity.completedReported = true;
    }
  }, [activity, config.experimentId, configReady, options, report, sessionId]);

  useLayoutEffect(() => {
    if (!options.eligible) return undefined;
    activate();
    return () => {
      cancelSession(sessionId, 'route_changed');
      deactivate();
    };
  }, [options.eligible, runKey, activate, deactivate, cancelSession, sessionId]);

  useEffect(() => {
    flushActivity();
  }, [flushActivity]);

  const reportGameplayStarted = useCallback(() => {
    activity.startRequested = true;
    flushActivity();
  }, [activity, flushActivity]);

  const reportQuestionAnswered = useCallback(
    (count: number) => {
      const boundedCount = clampInteger(count, MAX_ANSWERED_QUESTION_COUNT);
      if (boundedCount > 0) activity.questionCounts.add(boundedCount);
      flushActivity();
    },
    [activity, flushActivity]
  );

  const reportSessionCompleted = useCallback(() => {
    activity.completedRequested = true;
    flushActivity();
  }, [activity, flushActivity]);

  const bannerStatus: GameAdBannerStatus =
    !options.eligible ||
    !configReady ||
    !config.bannerEnabled ||
    !config.rolloutPercent ||
    !config.aggression
      ? 'disabled'
      : variant === 'control'
        ? 'control'
        : shouldShowGameAdOpportunity({
              userId: actorId || '',
              experimentId: config.experimentId,
              sessionId,
              format: 'banner',
              opportunityKey: 'banner-slot',
              aggression: config.aggression,
            })
          ? 'enabled'
          : 'not_sampled';
  const banner = useMemo(
    () => ({ enabled: bannerStatus === 'enabled', status: bannerStatus, label: LABEL }),
    [bannerStatus]
  );
  const bannerOpportunityRef = useRef({ sessionId, opportunityId: createGameAdUuid() });
  if (bannerOpportunityRef.current.sessionId !== sessionId) {
    bannerOpportunityRef.current = { sessionId, opportunityId: createGameAdUuid() };
  }
  const bannerOpportunityId = bannerOpportunityRef.current.opportunityId;
  const rewardOpportunityRef = useRef({
    sessionId,
    configVersion: config.version,
    opportunityId: createGameAdUuid(),
  });
  if (
    rewardOpportunityRef.current.sessionId !== sessionId ||
    rewardOpportunityRef.current.configVersion !== config.version
  ) {
    rewardOpportunityRef.current = {
      sessionId,
      configVersion: config.version,
      opportunityId: createGameAdUuid(),
    };
  }
  const rewardOpportunityId = rewardOpportunityRef.current.opportunityId;
  const rewardedOfferAvailable = Boolean(
    adsEnabled &&
    config.rewardedEnabled &&
    shouldShowGameAdOpportunity({
      userId: actorId || '',
      experimentId: config.experimentId,
      sessionId,
      format: 'rewarded',
      opportunityKey: 'streak-repair-offer',
      aggression: config.aggression,
    })
  );

  useEffect(() => {
    if (!options.eligible || !configReady || !options.userId || activity.bannerOfferReported)
      return;
    if (!config.rolloutPercent || !config.aggression || !config.bannerEnabled) return;
    const reason =
      bannerStatus === 'control'
        ? 'control_group'
        : bannerStatus === 'not_sampled'
          ? 'aggression_sampled_out'
          : undefined;
    recordOffer(
      buildEvent(sessionId, options, options.userId, config, variant, {
        eventType: 'offer',
        format: 'banner',
        placement: 'banner_slot',
        opportunityId: bannerOpportunityId,
        reason,
        answeredQuestionCount: options.answeredQuestionCount,
      })
    );
    activity.bannerOfferReported = true;
  }, [
    activity,
    bannerOpportunityId,
    bannerStatus,
    config,
    configReady,
    options,
    recordOffer,
    sessionId,
    variant,
  ]);

  const reportBannerImpression = useCallback(() => {
    if (activity.bannerImpressionReported || bannerStatus !== 'enabled') return;
    activity.bannerImpressionReported = true;
    report({
      eventType: 'banner_impression',
      format: 'banner',
      placement: 'banner_slot',
      opportunityId: bannerOpportunityId,
    });
  }, [activity, bannerOpportunityId, bannerStatus, report]);

  const reportBannerDismissed = useCallback(() => {
    if (activity.bannerDismissReported || bannerStatus !== 'enabled') return;
    activity.bannerDismissReported = true;
    report({
      eventType: 'banner_dismissed',
      format: 'banner',
      placement: 'banner_slot',
      opportunityId: bannerOpportunityId,
    });
  }, [activity, bannerOpportunityId, bannerStatus, report]);

  const reportStreakRepairOffer = useCallback(() => {
    if (!options.eligible || !configReady || !options.userId || !config.rewardedEnabled) return;
    if (config.mode !== 'test' || config.aggression === 0 || config.rolloutPercent === 0) return;
    if (activity.rewardOfferReportedVersion === config.version) return;
    const reason =
      variant === 'control'
        ? 'control_group'
        : rewardedOfferAvailable
          ? undefined
          : 'aggression_sampled_out';
    recordOffer(
      buildEvent(sessionId, options, options.userId, config, variant, {
        eventType: 'offer',
        format: 'rewarded',
        placement: 'streak_repair',
        opportunityId: rewardOpportunityId,
        reason,
        answeredQuestionCount: options.answeredQuestionCount,
      })
    );
    activity.rewardOfferReportedVersion = config.version;
  }, [
    activity,
    config,
    configReady,
    options,
    recordOffer,
    rewardedOfferAvailable,
    rewardOpportunityId,
    sessionId,
    variant,
  ]);

  const request = useCallback(
    (
      format: 'interstitial' | 'rewarded',
      placement: 'between_rounds' | 'continue_playing' | 'streak_repair',
      signal?: AbortSignal
    ) => {
      if (!options.eligible || !options.userId) {
        return Promise.resolve<GameAdOutcome>({
          status: 'disabled',
          requestId: createGameAdUuid(),
        });
      }
      return requestAd({
        sessionId,
        options,
        userId: options.userId,
        variant,
        format,
        placement,
        ...(format === 'rewarded' ? { opportunityId: rewardOpportunityId } : {}),
        signal,
      });
    },
    [requestAd, options, sessionId, variant, rewardOpportunityId]
  );

  const requestInterstitial = useCallback(
    (input: { placement: 'between_rounds' | 'continue_playing'; signal?: AbortSignal }) =>
      request('interstitial', input.placement, input.signal),
    [request]
  );
  const requestStreakRepair = useCallback(
    (input?: { signal?: AbortSignal }) => request('rewarded', 'streak_repair', input?.signal),
    [request]
  );

  const reportStreakRepairPreviewApplied = useCallback(
    (requestId: string) => {
      reportPreviewApplied(sessionId, requestId);
    },
    [reportPreviewApplied, sessionId]
  );

  return {
    sessionId,
    config: context.config,
    configReady: context.configReady,
    variant,
    adsEnabled,
    rewardedOfferAvailable,
    requestInterstitial,
    requestStreakRepair,
    reportStreakRepairOffer,
    reportGameplayStarted,
    reportQuestionAnswered,
    reportSessionCompleted,
    reportStreakRepairPreviewApplied,
    banner,
    reportBannerImpression,
    reportBannerDismissed,
  };
}
